import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState, formatMoney } from "../components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Ship, Plus, Trash2, X, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { SHIPMENT_STATUSES, SHIPMENT_STATUS_LABELS, ShipmentStatusBadge } from "./Imports";

const fmtTL = (v) => `TRY ${(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD = (v) => `USD ${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ImportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sh, setSh] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/shipments/${id}`).then((r) => setSh(r.data)).catch(() => navigate("/imports"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    try { await api.put(`/shipments/${id}`, patch); toast.success("Kaydedildi"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const setStatus = async (st) => {
    try {
      await api.put(`/shipments/${id}/status`, { status: st });
      toast.success(`Durum güncellendi → ${SHIPMENT_STATUS_LABELS[st]}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Durum güncellenemedi"); }
  };

  const patchItem = async (it, patch) => {
    try {
      await api.put(`/shipments/${id}/items/${it.order_id}/${it.item_id}`, patch);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`${it.part_number} yüklemenin dışına çıkarılsın mı?`)) return;
    try {
      await api.delete(`/shipments/${id}/items/${it.order_id}/${it.item_id}`);
      toast.success("Kalem çıkarıldı"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Çıkarılamadı"); }
  };

  const deleteShipment = async () => {
    if (!window.confirm(`"${sh.name}" yükleme kaydı silinsin mi?`)) return;
    try { await api.delete(`/shipments/${id}`); toast.success("Silindi"); navigate("/imports"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi"); }
  };

  const downloadBlob = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Dosya oluşturulamadı"); }
  };

  const uploadItemsExcel = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/shipments/${id}/items/import`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.updated} kalem güncellendi · ${data.catalog_touched} ürün katalogu dolduruldu${data.skipped ? ` · ${data.skipped} satır atlandı` : ""}`,
        { duration: 7000 });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import başarısız"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!sh) return <PageBody>Loading…</PageBody>;
  const items = sh.items || [];
  const totalQty = items.reduce((a, i) => a + (i.quantity || 0), 0);
  const totalWeight = items.reduce((a, i) => a + ((i.unit_weight ?? 0) * (i.quantity || 0)), 0);
  const safeName = (sh.name || "shipment").replace(/\s+/g, "_");
  const norm = sh.cost_normalized || {};
  const costByCur = Object.entries(sh.cost_totals || {}).map(([c, v]) => formatMoney(v, c)).join(" + ");

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-3"><Ship className="w-6 h-6 text-blue-700" /> {sh.name}</span>}
        subtitle={
          <span>Yükleme · {items.length} kalem · {totalQty} adet{totalWeight ? ` · ${totalWeight.toFixed(3)} kg` : ""}</span>
        }
        testid="import-detail-title"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => downloadBlob(`/shipments/${id}/items.xlsx`, `ShipmentItems_${safeName}.xlsx`)}
                    variant="outline" className="rounded-none border-slate-300 hover:bg-slate-100"
                    data-testid="shipment-items-export-btn" disabled={!items.length}>
              <Download className="w-4 h-4 mr-2" /> Items Excel
            </Button>
            <Button onClick={() => fileRef.current?.click()} variant="outline"
                    className="rounded-none border-slate-300 hover:bg-slate-100" data-testid="shipment-items-import-btn">
              <Upload className="w-4 h-4 mr-2" /> Items Yükle
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
                   onChange={(e) => uploadItemsExcel(e.target.files?.[0])} />
            <Button onClick={() => downloadBlob(`/shipments/${id}/packing-list.xlsx`, `PackingList_${safeName}.xlsx`)}
                    variant="outline" className="rounded-none border-slate-300 hover:bg-slate-100"
                    data-testid="packing-list-xlsx-btn" disabled={!items.length}>
              <Download className="w-4 h-4 mr-2" /> Packing XLS
            </Button>
            <Button onClick={() => downloadBlob(`/shipments/${id}/packing-list.pdf`, `PackingList_${safeName}.pdf`)}
                    variant="outline" className="rounded-none border-slate-300 hover:bg-slate-100"
                    data-testid="packing-list-pdf-btn" disabled={!items.length}>
              <Download className="w-4 h-4 mr-2" /> Packing PDF
            </Button>
            <Button onClick={() => setAddOpen(true)} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="add-items-btn">
              <Plus className="w-4 h-4 mr-2" /> Add items
            </Button>
            <Button onClick={deleteShipment} variant="outline" className="rounded-none border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-shipment-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        }
      />
      <PageBody>
        {/* Üst meta: ad / tarih / not / durum */}
        <div className="grid md:grid-cols-5 gap-3 mb-4 items-end">
          <div>
            <Label className="label-caps">Yükleme adı</Label>
            <Input defaultValue={sh.name} key={`nm-${sh.id}-${sh.name}`}
              onBlur={(e) => { if (e.target.value !== sh.name) save({ name: e.target.value }); }}
              className="rounded-none mt-1 font-mono" data-testid="shipment-name-edit" />
          </div>
          <div>
            <Label className="label-caps">Yükleme tarihi</Label>
            <input type="date" defaultValue={sh.shipment_date || ""} key={`sd-${sh.id}-${sh.shipment_date || ""}`}
              onBlur={(e) => { if ((e.target.value || null) !== (sh.shipment_date || null)) save({ shipment_date: e.target.value || null }); }}
              className="w-full h-9 rounded-none border border-slate-300 px-2 font-mono text-xs bg-white mt-1"
              data-testid="shipment-date" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-caps">Not</Label>
            <Input defaultValue={sh.notes || ""} key={`nt-${sh.id}-${sh.notes || ""}`}
              onBlur={(e) => { if (e.target.value !== (sh.notes || "")) save({ notes: e.target.value || null }); }}
              placeholder="Gümrük notu, nakliye firması…" className="rounded-none mt-1" data-testid="shipment-notes-edit" />
          </div>
          <div>
            <Label className="label-caps">Durum</Label>
            <div className="flex items-center gap-2 mt-1">
              <ShipmentStatusBadge status={sh.status} />
              <Select value={sh.status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-none h-9 flex-1" data-testid="shipment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {SHIPMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{SHIPMENT_STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Koli & ağırlık */}
        <div className="grid md:grid-cols-3 gap-3 mb-4 items-end">
          {[["Koli sayısı", "box_count", "örn. 12", "shipment-box-count"],
            ["Brüt ağırlık (kg)", "gross_weight", "örn. 154.5", "shipment-gross-weight"],
            ["Net ağırlık (kg)", "net_weight", "örn. 138.2", "shipment-net-weight"]].map(([lab, key, ph, tid]) => (
            <div key={key}>
              <Label className="label-caps">{lab}</Label>
              <Input type="text" inputMode="decimal"
                defaultValue={sh[key] == null ? "" : String(sh[key])} key={`${key}-${sh.id}-${sh[key] ?? ""}`}
                onBlur={(e) => {
                  const raw = e.target.value.trim().replace(",", ".");
                  const cur = sh[key] == null ? "" : String(sh[key]);
                  if (raw === cur) return;
                  const v = raw === "" ? null : parseFloat(raw);
                  if (raw !== "" && Number.isNaN(v)) { toast.error("Geçerli sayı girin"); return; }
                  save({ [key]: v });
                }}
                placeholder={ph} className="rounded-none mt-1 font-mono" data-testid={tid} />
            </div>
          ))}
        </div>

        {/* Yükleme bedeli & gümrükleme */}
        <div className="grid md:grid-cols-2 gap-3 mb-4 items-end">
          {[["Yükleme bedeli (navlun)", "freight_cost", "freight_currency", "shipment-freight-cost", "shipment-freight-currency", "örn. 850"],
            ["Gümrükleme bedeli", "customs_cost", "customs_currency", "shipment-customs-cost", "shipment-customs-currency", "örn. 1200"]].map(
            ([lab, kAmt, kCur, tAmt, tCur, ph]) => (
            <div key={kAmt}>
              <Label className="label-caps">{lab}</Label>
              <div className="flex gap-2 mt-1">
                <Input type="text" inputMode="decimal"
                  defaultValue={sh[kAmt] == null ? "" : String(sh[kAmt])} key={`${kAmt}-${sh.id}-${sh[kAmt] ?? ""}`}
                  onBlur={(e) => {
                    const raw = e.target.value.trim().replace(",", ".");
                    const cur = sh[kAmt] == null ? "" : String(sh[kAmt]);
                    if (raw === cur) return;
                    const v = raw === "" ? null : parseFloat(raw);
                    if (raw !== "" && Number.isNaN(v)) { toast.error("Geçerli sayı girin"); return; }
                    save({ [kAmt]: v });
                  }}
                  placeholder={ph} className="rounded-none font-mono" data-testid={tAmt} />
                <Select value={sh[kCur] || "USD"} onValueChange={(v) => save({ [kCur]: v })}>
                  <SelectTrigger className="rounded-none w-24 font-mono text-xs" data-testid={tCur}><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {["USD", "EUR", "TRY", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>

        {/* Toplam maliyet özeti */}
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="border border-slate-300 p-4 bg-white">
            <div className="label-caps text-slate-500 mb-1">Toplam Maliyet · USD</div>
            <div className="text-2xl font-display tracking-tight" data-testid="cost-total-usd">{fmtUSD(norm.usd)}</div>
          </div>
          <div className="border border-slate-300 p-4 bg-white">
            <div className="label-caps text-slate-500 mb-1">Toplam Maliyet · TL</div>
            <div className="text-2xl font-display tracking-tight" data-testid="cost-total-try">{fmtTL(norm.try)}</div>
          </div>
          <div className="border border-slate-300 p-4 bg-slate-50 text-xs text-slate-600 space-y-1">
            <div className="flex items-center justify-between">
              <div className="label-caps text-slate-500">TCMB kurları</div>
              {norm.locked ? (
                <span className="inline-block px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] font-medium uppercase tracking-wider" data-testid="rates-locked-badge">KİLİTLİ</span>
              ) : (
                <Button onClick={async () => { try { await api.post(`/shipments/${id}/lock-rates`); toast.success("Kurlar kilitlendi"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Kilitlenemedi"); } }}
                  variant="outline" className="h-6 rounded-none text-[10px] border-slate-300 px-2" data-testid="lock-rates-btn">Kurları kilitle</Button>
              )}
            </div>
            {norm.rate_date && <div className="font-mono">{norm.rate_date}</div>}
            {Object.entries(norm.rates || {}).map(([c, r]) => (
              <div key={c} className="font-mono">1 {c} = {Number(r).toFixed(4)} ₺</div>
            ))}
            {costByCur && <div className="pt-1 border-t border-slate-200 font-mono text-slate-500">Ham: {costByCur}</div>}
          </div>
        </div>

        {/* Dağıtım yöntemi + rapor */}
        <div className="flex items-center gap-3 mb-5 text-xs">
          <Label className="label-caps">Navlun+gümrük dağıtımı</Label>
          <Select value={sh.allocation_mode || "value"} onValueChange={(v) => save({ allocation_mode: v })}>
            <SelectTrigger className="rounded-none w-44 h-8 text-xs" data-testid="allocation-mode"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="value">Değer oranında</SelectItem>
              <SelectItem value="weight">Ağırlık oranında</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => downloadBlob(`/shipments/${id}/cost-report.pdf`, `CostReport_${safeName}.pdf`)}
            variant="outline" className="rounded-none h-8 border-slate-300 ml-auto" data-testid="cost-report-btn" disabled={!items.length}>
            <Download className="w-4 h-4 mr-2" /> Maliyet Raporu PDF
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState title="Kalem yok" hint='"Add items" ile ürün ekleyin veya Items Excel ile toplu doldurun.' />
        ) : (
          <div className="bg-white border border-slate-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Order</th>
                <th className="py-2 px-3 label-caps">Part #</th>
                <th className="py-2 px-3 label-caps">Tanım</th>
                <th className="py-2 px-3 label-caps">GTIP</th>
                <th className="py-2 px-3 label-caps">Menşei</th>
                <th className="py-2 px-3 label-caps text-right">Adet</th>
                <th className="py-2 px-3 label-caps text-right">Birim Ağ. (kg)</th>
                <th className="py-2 px-3 label-caps text-right">Toplam Ağ.</th>
                <th className="py-2 px-3 label-caps text-right">Alış</th>
                <th className="py-2 px-3 label-caps text-right">Vergi %</th>
                <th className="py-2 px-3 label-caps text-right">Kalem Maliyeti</th>
                <th className="py-2 px-3 label-caps text-right">Birim TL</th>
                <th className="py-2 px-3 label-caps w-8"></th>
              </tr></thead>
              <tbody>
                {items.map((it) => {
                  const twt = (it.unit_weight ?? null) == null ? null : (it.unit_weight * (it.quantity || 0));
                  return (
                    <tr key={`${it.order_id}-${it.item_id}`} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`shipment-item-${it.item_id}`}>
                      <td className="py-2 px-3 font-mono"><Link to={`/orders/${it.order_id}`} className="text-blue-700 hover:text-blue-900">{it.order_number}</Link></td>
                      <td className="py-2 px-3 font-mono">{it.part_number}</td>
                      <td className="py-2 px-3 text-xs max-w-48 truncate" title={it.description || ""}>{it.description || "-"}</td>
                      <td className="py-2 px-3">
                        <NumericCell value={it.gtip || ""} testId={`gtip-${it.item_id}`} width={28} mono
                          onCommit={(v) => patchItem(it, { gtip: v || null })} placeholder="8542.31…" />
                      </td>
                      <td className="py-2 px-3">
                        <NumericCell value={it.origin || ""} testId={`origin-${it.item_id}`} width={16}
                          onCommit={(v) => patchItem(it, { origin: v || null })} placeholder="CN" />
                      </td>
                      <td className="py-2 px-3 font-mono text-right">{it.quantity}</td>
                      <td className="py-2 px-3 text-right">
                        <NumericCell value={it.unit_weight ?? ""} testId={`unit-weight-${it.item_id}`} width={20} mono numeric
                          onCommit={(v) => patchItem(it, { unit_weight: v === "" ? null : parseFloat(v) })} placeholder="0.012" />
                      </td>
                      <td className="py-2 px-3 font-mono text-right text-xs text-slate-600">
                        {twt == null ? "—" : `${twt.toFixed(3)} kg`}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-1">
                          <NumericCell value={it.purchase_price ?? ""} testId={`purchase-price-${it.item_id}`} width={20} mono numeric
                            onCommit={(v) => patchItem(it, { purchase_price: v === "" ? null : parseFloat(v) })} placeholder="0.00" />
                          <select value={it.purchase_currency || "USD"}
                            onChange={(e) => patchItem(it, { purchase_currency: e.target.value })}
                            className="h-8 rounded-none border border-slate-300 px-1 text-xs bg-white font-mono"
                            data-testid={`purchase-currency-${it.item_id}`}>
                            {["USD", "EUR", "TRY", "GBP"].map((c) => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        <NumericCell value={it.tax_percent ?? ""} testId={`tax-percent-${it.item_id}`} width={16} mono numeric
                          onCommit={(v) => patchItem(it, { tax_percent: v === "" ? null : parseFloat(v) })} placeholder="0" />
                      </td>
                      <td className="py-2 px-3 font-mono text-right" data-testid={`line-cost-${it.item_id}`}>
                        {it.line_cost == null ? <span className="text-slate-400">—</span>
                          : formatMoney(it.line_cost, it.purchase_currency || it.currency)}
                      </td>
                      <td className="py-2 px-3 font-mono text-right" data-testid={`unit-cost-try-${it.item_id}`}>
                        {it.unit_cost_try == null ? <span className="text-slate-400">—</span>
                          : <>
                              <div>{Number(it.unit_cost_try).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺</div>
                              <div className="text-[10px] text-slate-500">
                                +{Number((it.freight_share_try || 0) + (it.customs_share_try || 0)).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺ pay
                              </div>
                            </>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button onClick={() => removeItem(it)} className="text-slate-400 hover:text-red-600" data-testid={`remove-item-${it.item_id}`} title="Çıkar">
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>

      <AddItemsDialog shipment={sh} open={addOpen} onClose={() => { setAddOpen(false); load(); }} />
    </>
  );
}

function NumericCell({ value, onCommit, placeholder, testId, width = 20, mono = false, numeric = false }) {
  const w = { 16: "w-16", 20: "w-20", 28: "w-28" }[width] || "w-20";
  return (
    <input
      type="text"
      inputMode={numeric ? "decimal" : "text"}
      defaultValue={value}
      key={`${testId}-${value}`}
      onBlur={(e) => {
        const raw = e.target.value.trim();
        const original = value === null || value === undefined ? "" : String(value);
        if (raw === original) return;
        if (numeric) {
          const norm = raw.replace(",", ".");
          if (norm !== "" && Number.isNaN(parseFloat(norm))) { toast.error("Geçerli sayı girin"); return; }
          onCommit(norm);
        } else {
          onCommit(raw);
        }
      }}
      placeholder={placeholder}
      className={`${w} h-8 rounded-none border border-slate-300 px-2 text-xs bg-white ${mono ? "font-mono" : ""}`}
      data-testid={testId}
    />
  );
}

function AddItemsDialog({ shipment, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel({}); setQ("");
    api.get("/order-items").then((r) => setRows(r.data));
  }, [open]);

  const inShipment = useMemo(() => new Set((shipment.items || []).map((i) => `${i.order_id}:${i.item_id}`)), [shipment]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return rows.filter((r) => !s ||
      (r.item.part_number || "").toLowerCase().includes(s) ||
      (r.order_number || "").toLowerCase().includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s));
  }, [rows, q]);

  const refs = Object.keys(sel).filter((k) => sel[k]).map((k) => {
    const [order_id, item_id] = k.split("|");
    return { order_id, item_id };
  });

  const submit = async () => {
    if (!refs.length) return toast.error("Önce kalem seçin");
    setBusy(true);
    try {
      const { data } = await api.post(`/shipments/${shipment.id}/items`, { items: refs });
      toast.success(`${data.added} kalem eklendi`);
      (data.errors || []).slice(0, 3).forEach((e) => toast.warning(e, { duration: 6000 }));
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eklenemedi"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-3xl" data-testid="add-items-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Sipariş kalemlerini seç</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Part #, order, müşteri ara…" className="rounded-none" data-testid="add-items-search" autoFocus />
          <div className="border border-slate-300 max-h-96 overflow-y-auto" data-testid="add-items-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300 sticky top-0"><tr className="text-left">
                <th className="py-2 px-3 w-8"></th>
                <th className="py-2 px-3 label-caps">Order</th>
                <th className="py-2 px-3 label-caps">Müşteri</th>
                <th className="py-2 px-3 label-caps">Part #</th>
                <th className="py-2 px-3 label-caps text-right">Adet</th>
                <th className="py-2 px-3 label-caps text-right">Satış</th>
                <th className="py-2 px-3 label-caps">Durum</th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => {
                  const key = `${r.order_id}|${r.item.id}`;
                  const already = inShipment.has(`${r.order_id}:${r.item.id}`);
                  return (
                    <tr key={key} className={`border-b border-slate-100 ${already ? "opacity-40" : "hover:bg-slate-50 cursor-pointer"}`}
                        onClick={() => !already && setSel((s) => ({ ...s, [key]: !s[key] }))}>
                      <td className="py-2 px-3">
                        <input type="checkbox" checked={!!sel[key]} disabled={already} onChange={() => {}} className="w-4 h-4" data-testid={`pick-${r.item.id}`} />
                      </td>
                      <td className="py-2 px-3 font-mono text-xs">{r.order_number}</td>
                      <td className="py-2 px-3 text-xs">{r.customer_name}</td>
                      <td className="py-2 px-3 font-mono">{r.item.part_number}</td>
                      <td className="py-2 px-3 font-mono text-right">{r.item.quantity}</td>
                      <td className="py-2 px-3 font-mono text-right">{formatMoney(r.item.unit_price, r.item.currency)}</td>
                      <td className="py-2 px-3">{already ? <span className="text-xs text-slate-400">bu yüklemede</span> : <StatusBadge status={r.item.status} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="add-items-cancel">Vazgeç</Button>
          <Button onClick={submit} disabled={busy || !refs.length} className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="add-items-submit">
            <Plus className="w-4 h-4 mr-2" /> {refs.length ? `${refs.length} kalem ekle` : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
